/**
 * ProjectArchivedBanner component for displaying archived project warning
 * Used on session/run pages when the associated project is archived
 */
import { Archive, X, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface ProjectArchivedBannerProps {
  /** Name of the archived project */
  projectName?: string
  /** Custom message to display */
  message?: string
  /** Whether the banner can be dismissed */
  dismissible?: boolean
  /** Callback when banner is dismissed */
  onDismiss?: () => void
  /** Visual variant */
  variant?: 'warning' | 'info'
  /** Additional CSS classes */
  className?: string
}

/**
 * Banner component that warns users when viewing content from an archived project.
 * Can be dismissible and supports different visual variants.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ProjectArchivedBanner projectName="Old Project" />
 *
 * // Dismissible
 * <ProjectArchivedBanner
 *   projectName="Legacy System"
 *   dismissible
 *   onDismiss={() => setDismissed(true)}
 * />
 *
 * // Custom message
 * <ProjectArchivedBanner
 *   projectName="Deprecated API"
 *   message="This project is archived. No new sessions can be created."
 * />
 * ```
 */
export function ProjectArchivedBanner({
  projectName,
  message,
  dismissible = false,
  onDismiss,
  variant = 'warning',
  className
}: ProjectArchivedBannerProps) {
  const defaultMessage = projectName
    ? `The project "${projectName}" is archived.`
    : 'This project is archived.'

  const displayMessage = message || defaultMessage

  const variantStyles = {
    warning: {
      container: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-200',
      icon: 'text-amber-600 dark:text-amber-400'
    },
    info: {
      container: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/50 dark:border-blue-800 dark:text-blue-200',
      icon: 'text-blue-600 dark:text-blue-400'
    }
  }

  const styles = variantStyles[variant]
  const Icon = variant === 'warning' ? Archive : Info

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg border',
        styles.container,
        className
      )}
    >
      <Icon
        className={cn('h-5 w-5 shrink-0', styles.icon)}
        data-testid="archive-icon"
        aria-hidden="true"
      />

      <p className="flex-1 text-sm font-medium">{displayMessage}</p>

      {dismissible && onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          aria-label="Dismiss banner"
          className={cn(
            'h-6 w-6 p-0 shrink-0',
            variant === 'warning'
              ? 'hover:bg-amber-200/50 dark:hover:bg-amber-800/50'
              : 'hover:bg-blue-200/50 dark:hover:bg-blue-800/50'
          )}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
